import { Injectable } from '@angular/core';

/** What discovery found on the workstation, and where to capture from. */
export interface RdDevice {
  port: number;
  /** Path the CAPTURE verb is served on, relative to https://localhost:<port>. */
  capturePath: string;
  deviceInfoPath: string;
  /** READY | NOTREADY — an unregistered L1 device reports NOTREADY and captures nothing. */
  status: string;
  /** Vendor string from the RDSERVICE response, e.g. "Mantra Softech India Pvt Ltd". */
  info: string;
  /** Model id from DEVICEINFO (@mi), e.g. MFS110. */
  model: string;
  /** Device code / serial from DEVICEINFO (@dc). */
  serial: string;
  /** RD Service version from DEVICEINFO (@rdsVer). */
  rdsVer: string;
  /** 'F' fingerprint | 'I' iris | '' unrecognised model. */
  family: string;
}

export type RdMode = 'FINGERPRINT' | 'IRIS';

/**
 * The UIDAI RD Service client — the only thing in the app that talks to a biometric scanner.
 *
 * This is NOT the same device family as `FingerprintService`, and the two must not be merged.
 * That one drives a raw-image scanner SDK over a localhost JSON API and hands back a bitmap for
 * enrolment. This one drives a UIDAI-certified L1 RD Service, which speaks custom HTTP verbs
 * (RDSERVICE / DEVICEINFO / CAPTURE) and hands back a *signed PID block* that only UIDAI can open.
 * Only the second is admissible for ABDM biometric authentication.
 *
 * Everything here happens on the operator's machine and never leaves it except as the resulting
 * PID block, which the dialog posts to our backend and the backend forwards to ABDM.
 *
 * Five things the vendor reference script does that this deliberately does not:
 *
 *  1. It imports `crypto-js/sha256` for the wadh. This repo aliases `crypto-js` to a shim that
 *     re-exports only AES, MD5, enc, pad, mode and HmacSHA512, so that import does not resolve.
 *     Native `crypto.subtle.digest` is used instead — no bundle cost, and no shared shim to widen.
 *     (`crypto.subtle` needs a secure context; http://localhost counts as one, as does https.)
 *  2. It captures with a *synchronous* XHR, freezing the tab for the device's full 10s timeout.
 *     Every call here is async. `fetch` cannot be used at all — it rejects non-standard verbs.
 *  3. It fires 21 parallel probes on every open and `alert()`s on failure. This probes the cached
 *     port first and otherwise scans sequentially, and it throws instead of alerting so the dialog
 *     can render the failure.
 *  4. It infers iris-vs-fingerprint from the model string, with a test (`MFS100`) that the ticket's
 *     own MFS110 does not match. Here the caller's authMode decides, and DEVICEINFO is used only to
 *     refuse a clearly mismatched device.
 *  5. It leaves the port cache commented out.
 */
@Injectable({ providedIn: 'root' })
export class RdServiceService {

  private static readonly PORT_START = 11100;
  private static readonly PORT_END = 11120;
  private static readonly CACHE_KEY = 'rd_device_port';

  /** A probe must fail fast — most ports in the range have nothing listening. */
  private static readonly PROBE_TIMEOUT_MS = 3000;
  private static readonly INFO_TIMEOUT_MS = 5000;
  /** The device itself waits ~10s for a finger; allow for that plus signing. */
  private static readonly CAPTURE_TIMEOUT_MS = 20000;

  /** Models known to be iris readers. Anything else is assumed to be a fingerprint sensor. */
  private static readonly IRIS_MODELS = ['EPI1000', 'IRITECH', 'MIS100V2', 'IRISHIELD'];

  /**
   * Finds a ready RD Service and describes the attached device.
   *
   * @param mode what the operator asked for — decides which device family is acceptable.
   */
  async discover(mode: RdMode): Promise<RdDevice> {
    const cached = this.cachedPort();
    const ports: number[] = [];
    if (cached) {
      ports.push(cached);
    }
    for (let p = RdServiceService.PORT_START; p <= RdServiceService.PORT_END; p++) {
      if (p !== cached) {
        ports.push(p);
      }
    }

    let sawNotReady = false;
    // A port that answered at the HTTP level proves TLS and CORS are fine and something is
    // listening — which points somewhere completely different from a silent range.
    let respondingPort = 0;
    for (const port of ports) {
      let xml: string;
      try {
        xml = await this.request('RDSERVICE', this.url(port, ''), null, RdServiceService.PROBE_TIMEOUT_MS);
      } catch (e) {
        if (e && (e as any).rdStatus > 0 && !respondingPort) {
          respondingPort = port;
        }
        continue; // nothing listening here, or it is not an RD Service
      }
      // The verb is RDSERVICE but the element it answers with is <RDService> — the case does
      // NOT match, so this comparison has to be case-insensitive or every real scanner is
      // rejected here and discovery reports the range as silent.
      if (!xml || xml.toUpperCase().indexOf('RDSERVICE') < 0) {
        // Answered 200, but not as an RD Service. That is the strongest evidence of all that
        // something else holds the port — and without this it is the one case that falls all
        // the way through to "nothing answered", which is the opposite of what happened.
        if (!respondingPort) {
          respondingPort = port;
        }
        continue;
      }
      if (xml.indexOf('NOTREADY') >= 0 || xml.indexOf('READY') < 0) {
        // A service is running but the device is not usable — almost always an unregistered
        // L1 device or one that is not plugged in. Worth telling the operator precisely.
        sawNotReady = true;
        continue;
      }

      const device = this.parseRdService(port, xml);
      this.cachePort(port);
      await this.readDeviceInfo(device);
      this.assertServes(device, mode);
      return device;
    }

    this.forgetPort();
    const range = RdServiceService.PORT_START + '-' + RdServiceService.PORT_END;
    if (sawNotReady) {
      throw new Error('The RD Service is running but reports the scanner as not ready. '
        + 'Check that the device is plugged in and that its L1 registration has been completed '
        + 'in RD Service Manager.');
    }
    if (respondingPort) {
      throw new Error('Something is listening on https://localhost:' + respondingPort
        + ' but it did not answer as an RD Service. Check that the L1 RD Service for this scanner '
        + 'is the service running on that port.');
    }
    throw new Error('No RD Service answered on ports ' + range + ' on the machine running this browser. '
      + 'Either the L1 RD Service is not installed or running, or the browser has not been allowed to '
      + 'reach it. Check that RD Service Manager shows Ready, then open https://localhost:'
      + RdServiceService.PORT_START + ' once in this browser and accept the certificate. '
      + 'Note the RD Service must be installed on the machine running the browser, not on the server.');
  }

  /**
   * Captures one PID block and returns it base64-encoded, ready to post as-is.
   *
   * The signed XML is base64ed whole — never parsed apart, never reshaped. It is a signature over
   * its own bytes, so anything other than a verbatim round-trip invalidates it at UIDAI.
   */
  async capture(device: RdDevice, mode: RdMode): Promise<string> {
    const iris = mode === 'IRIS';
    const wadh = await this.wadh(iris ? 'I' : 'F');
    const opts = iris
      ? '<Opts env="P" fCount="0" fType="0" iCount="1" iType="0" pCount="0" pType="0" format="0" pidVer="2.0" '
        + 'wadh="' + wadh + '" posh="UNKNOWN" timeout="10000"/>'
      : '<Opts env="P" fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0" '
        + 'wadh="' + wadh + '" posh="UNKNOWN" timeout="10000"/>';
    const inputXml = '<PidOptions ver="1.0">' + opts + '</PidOptions>';

    const response = await this.request(
      'CAPTURE', this.url(device.port, device.capturePath), inputXml, RdServiceService.CAPTURE_TIMEOUT_MS);

    this.assertCaptureSucceeded(response);
    return this.toBase64Utf8(response);
  }

  /** Clears the remembered port, so the next discover() rescans. */
  forgetPort(): void {
    try {
      window.localStorage.removeItem(RdServiceService.CACHE_KEY);
    } catch (e) {
      // storage disabled — discovery just costs a scan every time
    }
  }

  // ------------------------------------------------------------------ internals

  private url(port: number, path: string): string {
    const suffix = !path ? '' : (path.charAt(0) === '/' ? path : '/' + path);
    return 'https://localhost:' + port + suffix;
  }

  /**
   * One RD Service call. Async XHR rather than fetch: the RD Service is addressed with custom
   * verbs, which fetch refuses outright.
   *
   * A failure here is indistinguishable at the JS level between "nothing listening", "certificate
   * not trusted" and "CORS preflight refused" — the browser reports all three as a bare network
   * error. That is why the discovery message names all of them.
   */
  private request(verb: string, url: string, body: string | null, timeoutMs: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let xhr: XMLHttpRequest;
      try {
        xhr = new XMLHttpRequest();
        xhr.open(verb, url, true);
      } catch (e) {
        reject(new Error('Could not open a ' + verb + ' request to ' + url));
        return;
      }
      xhr.timeout = timeoutMs;
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.responseText || '');
        } else {
          reject(this.transportError('The scanner returned HTTP ' + xhr.status + ' for ' + verb + '.', xhr.status));
        }
      };
      // status 0 means the request never completed at the HTTP level. The browser reports
      // "nothing listening", "certificate not trusted" and "CORS preflight refused" identically,
      // so this cannot be narrowed further here - only counted, and reported honestly.
      xhr.onerror = () => reject(this.transportError('Could not reach the RD Service at ' + url + '.', 0));
      xhr.ontimeout = () => reject(this.transportError('The RD Service did not respond in time at ' + url + '.', 0));
      xhr.onabort = () => reject(this.transportError('The ' + verb + ' request was cancelled.', 0));
      try {
        if (body === null) {
          xhr.send();
        } else {
          xhr.setRequestHeader('Content-Type', 'text/xml');
          xhr.send(body);
        }
      } catch (e) {
        reject(new Error('Could not send the ' + verb + ' request to ' + url + '.'));
      }
    });
  }

  /** An Error carrying the transport status, so discover() can classify the failure. */
  private transportError(message: string, status: number): Error {
    const e: any = new Error(message);
    e.rdStatus = status;
    return e;
  }

  private parseRdService(port: number, xml: string): RdDevice {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const root = doc.getElementsByTagName('RDService')[0];
    const device: RdDevice = {
      port,
      capturePath: '/rd/capture',
      deviceInfoPath: '/rd/info',
      status: root ? (root.getAttribute('status') || '') : '',
      info: root ? (root.getAttribute('info') || '') : '',
      model: '',
      serial: '',
      rdsVer: '',
      family: ''
    };
    const interfaces = doc.getElementsByTagName('Interface');
    for (let i = 0; i < interfaces.length; i++) {
      const id = interfaces[i].getAttribute('id');
      const path = interfaces[i].getAttribute('path') || '';
      if (id === 'CAPTURE' && path) {
        device.capturePath = path;
      } else if (id === 'DEVICEINFO' && path) {
        device.deviceInfoPath = path;
      }
    }
    return device;
  }

  /**
   * DEVICEINFO names the physical device. It is best-effort: a device that captures but does not
   * describe itself is still usable, so a failure here degrades to an unnamed device rather than
   * failing discovery.
   */
  private async readDeviceInfo(device: RdDevice): Promise<void> {
    let xml: string;
    try {
      xml = await this.request(
        'DEVICEINFO', this.url(device.port, device.deviceInfoPath), null, RdServiceService.INFO_TIMEOUT_MS);
    } catch (e) {
      return;
    }
    if (!xml) {
      return;
    }
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const node = doc.getElementsByTagName('DeviceInfo')[0];
    if (!node) {
      return;
    }
    device.model = node.getAttribute('mi') || '';
    device.serial = node.getAttribute('dc') || '';
    device.rdsVer = node.getAttribute('rdsVer') || '';
    device.family = this.familyOf(device.model);
  }

  /** '' when the model is not recognised — an unknown device is allowed, not blocked. */
  private familyOf(model: string): string {
    const probe = (model || '').toUpperCase();
    if (!probe) {
      return '';
    }
    for (const iris of RdServiceService.IRIS_MODELS) {
      if (probe.indexOf(iris) >= 0) {
        return 'I';
      }
    }
    return probe.indexOf('MFS') >= 0 || probe.indexOf('MANTRA') >= 0 ? 'F' : '';
  }

  /**
   * Refuses a device that plainly cannot serve what the operator picked — an iris authentication
   * against a fingerprint sensor produces a PID block that fails opaquely at UIDAI, far from here.
   * An unrecognised model is allowed through rather than blocked on an incomplete list.
   */
  private assertServes(device: RdDevice, mode: RdMode): void {
    const wanted = mode === 'IRIS' ? 'I' : 'F';
    if (device.family && device.family !== wanted) {
      const attached = device.family === 'I' ? 'an iris scanner' : 'a fingerprint scanner';
      const asked = mode === 'IRIS' ? 'IRIS Scan' : 'Fingerprint';
      throw new Error('The device attached to this machine is ' + attached
        + (device.model ? ' (' + device.model + ')' : '')
        + ', which cannot perform ' + asked + ' authentication. '
        + 'Attach the right scanner or change the authentication type.');
    }
  }

  /**
   * The wadh binds the capture to the auth parameters UIDAI will verify it under. Per the ABDM
   * material: SHA-256 over "2.5" + ra + rc + lr + de + pfr, base64ed — with lr = "Y".
   *
   * lr = "Y" is unusual (the common Aadhaar-auth value is "N") but is what the supplied
   * documentation specifies, so it is taken as normative. A wrong wadh is NOT rejected by the
   * device: it captures happily and fails later at UIDAI as an opaque verification error. Confirm
   * against the ABDM sandbox on the first end-to-end run before treating a verify failure as a bug.
   */
  private async wadh(ra: string): Promise<string> {
    // WebCrypto only exists in a secure context: https, or http on localhost / 127.0.0.1.
    // Reaching the app over a LAN address (http://192.168.x.x:4599) silently removes
    // crypto.subtle, and without this guard that surfaces as an unreadable TypeError from
    // inside a capture. This matters in the exact setup the operators use: when the RD Service
    // runs on a different machine or a VM, the browser has to sit beside it, and the temptation
    // is to load the app by IP. Port-forward it to localhost, or serve the app over https.
    if (!crypto || !crypto.subtle) {
      throw new Error('This page is not running in a secure context, so the browser will not '
        + 'perform the cryptography a biometric capture needs. Open the application over https, '
        + 'or as http://localhost — reaching it by IP address disables it.');
    }
    const text = '2.5' + ra + 'Y' + 'Y' + 'N' + 'N'; // ra + rc + lr + de + pfr
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return this.toBase64Bytes(new Uint8Array(digest));
  }

  /** The PID XML says whether the scan actually worked; a non-zero errCode is still HTTP 200. */
  private assertCaptureSucceeded(xml: string): void {
    if (!xml) {
      throw new Error('The scanner returned an empty response. Try the capture again.');
    }
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const resp = doc.getElementsByTagName('Resp')[0];
    if (!resp) {
      throw new Error('The scanner returned a response that could not be read as PID data.');
    }
    const code = resp.getAttribute('errCode');
    if (code && code !== '0') {
      const info = resp.getAttribute('errInfo') || '';
      throw new Error('The scan failed' + (info ? ': ' + info : '') + ' (error ' + code + ').');
    }
  }

  private cachedPort(): number | null {
    try {
      const raw = window.localStorage.getItem(RdServiceService.CACHE_KEY);
      const port = raw ? parseInt(raw, 10) : NaN;
      return port >= RdServiceService.PORT_START && port <= RdServiceService.PORT_END ? port : null;
    } catch (e) {
      return null;
    }
  }

  private cachePort(port: number): void {
    try {
      window.localStorage.setItem(RdServiceService.CACHE_KEY, String(port));
    } catch (e) {
      // storage disabled — discovery just costs a scan every time
    }
  }

  private toBase64Bytes(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /** btoa cannot take non-Latin-1 directly, so the XML is UTF-8 encoded first. */
  private toBase64Utf8(text: string): string {
    return this.toBase64Bytes(new TextEncoder().encode(text));
  }
}
